import {useCallback, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {MarkdownText} from "@pg-rp/markdown-text";

import {usePostPropertyFaqAnswerMutation} from "../../api/post_property_faq_answer";
import {PROPERTY_FAQ_SERVER_ERROR_MESSAGE} from "../../constants/property_faq_questions";
import {propertyFaqAnalytics} from "../../tracking/property_faq_tracking";
import {IPropertyFaqItem} from "../../types/IPropertyFaq";
import {faqAccordionContentWrapStyle, faqAccordionHeaderAnimationStyle} from "./property_faq_accordion_animation";
import {PropertyDetailFaqAnswerActions} from "./PropertyDetailFaqAnswerActions";
import {PropertyDetailFaqAnswerGate} from "./PropertyDetailFaqAnswerGate";
import {PropertyDetailFaqCommute} from "./PropertyDetailFaqCommute";
import {PropertyDetailFaqLoadingStatus} from "./PropertyDetailFaqLoadingStatus";

interface IProps {
    faqItem: IPropertyFaqItem;
    questionPosition: number;
    canSeeAnswer: boolean;
    propertyId: number;
    offerId: number;
    offerName: string;
    vendorId: number;
    vendorName: string;
    geoPointCoordinates: [number, number];
    onOpenMapModal?: () => void;
}

export const PropertyDetailFaqItem = (props: IProps) => {
    const {faqItem, questionPosition, canSeeAnswer, propertyId, offerId, offerName, vendorId, vendorName, geoPointCoordinates, onOpenMapModal} = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const previousExpandedStateRef = useRef(false);
    const [isRetryCooldownOver, setIsRetryCooldownOver] = useState(false);
    const [postPropertyFaqAnswer, answerRequest] = usePostPropertyFaqAnswerMutation();
    const pendingExpandTrackingRef = useRef(false);
    const errorStatus = getPropertyFaqErrorStatus(answerRequest.error);
    const retryCooldownMs = getPropertyFaqRetryCooldownMs(errorStatus);
    const isRetryBlocked = retryCooldownMs > 0 && !isRetryCooldownOver;

    const trackExpandedQuestion = useCallback(
        (answerContent: string | null) => {
            propertyFaqAnalytics.amplitude.expandQuestion({
                answerContent,
                index: questionPosition - 1,
                itemId: offerId,
                propertyId,
                questionHeading: faqItem.question,
                questionType: "default"
            });
        },
        [faqItem.question, offerId, propertyId, questionPosition]
    );

    useEffect(() => {
        if (isExpanded && canSeeAnswer && faqItem.questionKey && answerRequest.isUninitialized) {
            void postPropertyFaqAnswer({propertyId, question: faqItem.questionKey});
        }
    }, [answerRequest.isUninitialized, canSeeAnswer, faqItem.questionKey, isExpanded, postPropertyFaqAnswer, propertyId]);

    useEffect(() => {
        if (retryCooldownMs === 0) {
            return;
        }

        const retryTimeoutId = setTimeout(() => setIsRetryCooldownOver(true), retryCooldownMs);

        return () => clearTimeout(retryTimeoutId);
    }, [retryCooldownMs]);

    useEffect(() => {
        if (!pendingExpandTrackingRef.current || (!answerRequest.isSuccess && !answerRequest.isError)) {
            return;
        }

        pendingExpandTrackingRef.current = false;
        const answerContent = answerRequest.isSuccess ? (answerRequest.data?.answer ?? null) : null;
        trackExpandedQuestion(answerContent);
    }, [answerRequest.data?.answer, answerRequest.isError, answerRequest.isSuccess, trackExpandedQuestion]);

    const handleAccordionChange = (expanded: boolean) => {
        setIsExpanded(expanded);

        // Accordion calls onChange on mount. Comparing with the known initial state also preserves a very early user click
        // that can happen before the mount effect is flushed.
        if (previousExpandedStateRef.current === expanded) {
            return;
        }

        previousExpandedStateRef.current = expanded;
        propertyFaqAnalytics.gtm.itemToggle(expanded ? "expand" : "collapse", faqItem.question, questionPosition);

        if (!expanded) {
            pendingExpandTrackingRef.current = false;
            return;
        }

        if (faqItem.kind === "commute" || !canSeeAnswer || answerRequest.isSuccess || answerRequest.isError) {
            trackExpandedQuestion(answerRequest.data?.answer ?? null);
        } else {
            pendingExpandTrackingRef.current = true;
        }
    };

    const renderContent = () => {
        // The commute calculator handles gating internally: the address form is open to everyone,
        // the registration gate appears only after submitting
        if (faqItem.kind === "commute") {
            return (
                <PropertyDetailFaqCommute
                    geoPointCoordinates={geoPointCoordinates}
                    onOpenMapModal={onOpenMapModal}
                    canSeeAnswer={canSeeAnswer}
                    propertyId={propertyId}
                    offerId={offerId}
                    questionHeading={faqItem.question}
                    questionIndex={questionPosition - 1}
                />
            );
        }

        if (!isExpanded) {
            return null;
        }

        // Answers to the remaining questions are gated behind a verified phone
        if (!canSeeAnswer) {
            return <PropertyDetailFaqAnswerGate propertyId={propertyId} offerId={offerId} />;
        }

        const questionKey = faqItem.questionKey;

        if (answerRequest.isUninitialized || answerRequest.isLoading) {
            return <PropertyDetailFaqLoadingStatus />;
        }

        if (answerRequest.isError || (answerRequest.isSuccess && !answerRequest.data?.answer)) {
            return (
                <div className={answerErrorStyle} role="alert">
                    <Text variant="body_copy_2" as="p">
                        {getPropertyFaqErrorMessage(errorStatus)}
                    </Text>

                    <Button
                        type="button"
                        variant="outlined_secondary"
                        size="small"
                        disabled={isRetryBlocked}
                        onClick={() => {
                            setIsRetryCooldownOver(false);
                            void postPropertyFaqAnswer({propertyId, question: questionKey});
                        }}
                    >
                        {getPropertyFaqRetryLabel(errorStatus, isRetryBlocked)}
                    </Button>
                </div>
            );
        }

        if (answerRequest.data?.answer) {
            return (
                <>
                    <MarkdownText markdown={answerRequest.data.answer} variant="body_copy_2" />

                    <PropertyDetailFaqAnswerActions
                        propertyId={propertyId}
                        offerId={offerId}
                        offerName={offerName}
                        vendorId={vendorId}
                        vendorName={vendorName}
                        questionHeading={faqItem.question}
                        questionIndex={questionPosition - 1}
                        questionType="default"
                    />
                </>
            );
        }

        return null;
    };

    return (
        <Accordion theme="gray" className={accordionStyle} onChange={handleAccordionChange}>
            <Accordion.Header className={cx(faqAccordionHeaderAnimationStyle, isExpanded ? headerExpandedStyle : headerCollapsedStyle)}>
                <Text variant="headline_6" as="h3">
                    {faqItem.question}
                </Text>
            </Accordion.Header>

            <Accordion.Content className={contentStyle} wrapClassName={faqAccordionContentWrapStyle}>
                {renderContent()}
            </Accordion.Content>
        </Accordion>
    );
};

const RATE_LIMIT_COOLDOWN_MS = 60_000;
// While the backend circuit breaker is open it answers 503 immediately without asking DSA, so an earlier retry cannot succeed.
const CIRCUIT_BREAKER_COOLDOWN_MS = 30_000;

const getPropertyFaqRetryCooldownMs = (errorStatus: number | null): number => {
    if (errorStatus === 429) {
        return RATE_LIMIT_COOLDOWN_MS;
    }

    return errorStatus === 503 ? CIRCUIT_BREAKER_COOLDOWN_MS : 0;
};

const getPropertyFaqErrorMessage = (errorStatus: number | null): string => {
    if (errorStatus === 429) {
        return "Osiągnięto limit pytań. Spróbuj ponownie za minutę.";
    }

    // 502 and 503 mean the DSA answer is unavailable, not that the request was wrong.
    if (errorStatus !== null && errorStatus >= 500 && errorStatus < 600) {
        return PROPERTY_FAQ_SERVER_ERROR_MESSAGE;
    }

    return "Nie udało się pobrać odpowiedzi. Spróbuj ponownie.";
};

const getPropertyFaqRetryLabel = (errorStatus: number | null, isRetryBlocked: boolean): string => {
    if (!isRetryBlocked) {
        return "Spróbuj ponownie";
    }

    return errorStatus === 429 ? "Spróbuj ponownie za minutę" : "Spróbuj ponownie za chwilę";
};

const getPropertyFaqErrorStatus = (error: unknown): number | null => {
    if (!error || typeof error !== "object") {
        return null;
    }

    const {status, originalStatus} = error as {status?: unknown; originalStatus?: unknown};

    if (typeof status === "number") {
        return status;
    }

    return typeof originalStatus === "number" ? originalStatus : null;
};

const accordionStyle = css({
    width: "100%"
});

const headerCollapsedStyle = css({
    borderRadius: 1
});

const headerExpandedStyle = css({
    backgroundColor: "var(--colors-background-100)"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    px: 2,
    pb: 2,
    backgroundColor: "var(--colors-background-100)"
});

const answerErrorStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 1
});
import {useCallback, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {MarkdownText} from "@pg-rp/markdown-text";

import {usePostPropertyFaqAnswerMutation} from "../../api/post_property_faq_answer";
import {PROPERTY_FAQ_SERVER_ERROR_MESSAGE} from "../../constants/property_faq_questions";
import {propertyFaqAnalytics} from "../../tracking/property_faq_tracking";
import {IPropertyFaqItem} from "../../types/IPropertyFaq";
import {faqAccordionContentWrapStyle, faqAccordionHeaderAnimationStyle} from "./property_faq_accordion_animation";
import {PropertyDetailFaqAnswerActions} from "./PropertyDetailFaqAnswerActions";
import {PropertyDetailFaqAnswerGate} from "./PropertyDetailFaqAnswerGate";
import {PropertyDetailFaqCommute} from "./PropertyDetailFaqCommute";
import {PropertyDetailFaqLoadingStatus} from "./PropertyDetailFaqLoadingStatus";

interface IProps {
    faqItem: IPropertyFaqItem;
    questionPosition: number;
    canSeeAnswer: boolean;
    propertyId: number;
    offerId: number;
    offerName: string;
    vendorId: number;
    vendorName: string;
    geoPointCoordinates: [number, number];
    onOpenMapModal?: () => void;
}

export const PropertyDetailFaqItem = (props: IProps) => {
    const {faqItem, questionPosition, canSeeAnswer, propertyId, offerId, offerName, vendorId, vendorName, geoPointCoordinates, onOpenMapModal} = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const previousExpandedStateRef = useRef(false);
    const [isRetryCooldownOver, setIsRetryCooldownOver] = useState(false);
    const [postPropertyFaqAnswer, answerRequest] = usePostPropertyFaqAnswerMutation();
    const pendingExpandTrackingRef = useRef(false);
    const errorStatus = getPropertyFaqErrorStatus(answerRequest.error);
    const retryCooldownMs = getPropertyFaqRetryCooldownMs(errorStatus);
    const isRetryBlocked = retryCooldownMs > 0 && !isRetryCooldownOver;

    const trackExpandedQuestion = useCallback(
        (answerContent: string | null) => {
            propertyFaqAnalytics.amplitude.expandQuestion({
                answerContent,
                index: questionPosition - 1,
                itemId: offerId,
                propertyId,
                questionHeading: faqItem.question,
                questionType: "default"
            });
        },
        [faqItem.question, offerId, propertyId, questionPosition]
    );

    useEffect(() => {
        if (isExpanded && canSeeAnswer && faqItem.questionKey && answerRequest.isUninitialized) {
            void postPropertyFaqAnswer({propertyId, question: faqItem.questionKey});
        }
    }, [answerRequest.isUninitialized, canSeeAnswer, faqItem.questionKey, isExpanded, postPropertyFaqAnswer, propertyId]);

    useEffect(() => {
        if (retryCooldownMs === 0) {
            return;
        }

        const retryTimeoutId = setTimeout(() => setIsRetryCooldownOver(true), retryCooldownMs);

        return () => clearTimeout(retryTimeoutId);
    }, [retryCooldownMs]);

    useEffect(() => {
        if (!pendingExpandTrackingRef.current || (!answerRequest.isSuccess && !answerRequest.isError)) {
            return;
        }

        pendingExpandTrackingRef.current = false;
        const answerContent = answerRequest.isSuccess ? (answerRequest.data?.answer ?? null) : null;
        trackExpandedQuestion(answerContent);
    }, [answerRequest.data?.answer, answerRequest.isError, answerRequest.isSuccess, trackExpandedQuestion]);

    const handleAccordionChange = (expanded: boolean) => {
        setIsExpanded(expanded);

        // Accordion calls onChange on mount. Comparing with the known initial state also preserves a very early user click
        // that can happen before the mount effect is flushed.
        if (previousExpandedStateRef.current === expanded) {
            return;
        }

        previousExpandedStateRef.current = expanded;
        propertyFaqAnalytics.gtm.itemToggle(expanded ? "expand" : "collapse", faqItem.question, questionPosition);

        if (!expanded) {
            pendingExpandTrackingRef.current = false;
            return;
        }

        if (faqItem.kind === "commute" || !canSeeAnswer || answerRequest.isSuccess || answerRequest.isError) {
            trackExpandedQuestion(answerRequest.data?.answer ?? null);
        } else {
            pendingExpandTrackingRef.current = true;
        }
    };

    const renderContent = () => {
        // The commute calculator handles gating internally: the address form is open to everyone,
        // the registration gate appears only after submitting
        if (faqItem.kind === "commute") {
            return (
                <PropertyDetailFaqCommute
                    geoPointCoordinates={geoPointCoordinates}
                    onOpenMapModal={onOpenMapModal}
                    canSeeAnswer={canSeeAnswer}
                    propertyId={propertyId}
                    offerId={offerId}
                    questionHeading={faqItem.question}
                    questionIndex={questionPosition - 1}
                />
            );
        }

        if (!isExpanded) {
            return null;
        }

        // Answers to the remaining questions are gated behind a verified phone
        if (!canSeeAnswer) {
            return <PropertyDetailFaqAnswerGate propertyId={propertyId} offerId={offerId} />;
        }

        const questionKey = faqItem.questionKey;

        if (answerRequest.isUninitialized || answerRequest.isLoading) {
            return <PropertyDetailFaqLoadingStatus />;
        }

        if (answerRequest.isError || (answerRequest.isSuccess && !answerRequest.data?.answer)) {
            return (
                <div className={answerErrorStyle} role="alert">
                    <Text variant="body_copy_2" as="p">
                        {getPropertyFaqErrorMessage(errorStatus)}
                    </Text>

                    <Button
                        type="button"
                        variant="outlined_secondary"
                        size="small"
                        disabled={isRetryBlocked}
                        onClick={() => {
                            setIsRetryCooldownOver(false);
                            void postPropertyFaqAnswer({propertyId, question: questionKey});
                        }}
                    >
                        {getPropertyFaqRetryLabel(errorStatus, isRetryBlocked)}
                    </Button>
                </div>
            );
        }

        if (answerRequest.data?.answer) {
            return (
                <>
                    <MarkdownText markdown={answerRequest.data.answer} variant="body_copy_2" />

                    <PropertyDetailFaqAnswerActions
                        propertyId={propertyId}
                        offerId={offerId}
                        offerName={offerName}
                        vendorId={vendorId}
                        vendorName={vendorName}
                        questionHeading={faqItem.question}
                        questionIndex={questionPosition - 1}
                        questionType="default"
                    />
                </>
            );
        }

        return null;
    };

    return (
        <Accordion theme="gray" className={accordionStyle} onChange={handleAccordionChange}>
            <Accordion.Header className={cx(faqAccordionHeaderAnimationStyle, isExpanded ? headerExpandedStyle : headerCollapsedStyle)}>
                <Text variant="headline_6" as="h3">
                    {faqItem.question}
                </Text>
            </Accordion.Header>

            <Accordion.Content className={contentStyle} wrapClassName={faqAccordionContentWrapStyle}>
                {renderContent()}
            </Accordion.Content>
        </Accordion>
    );
};

const RATE_LIMIT_COOLDOWN_MS = 60_000;
// While the backend circuit breaker is open it answers 503 immediately without asking DSA, so an earlier retry cannot succeed.
const CIRCUIT_BREAKER_COOLDOWN_MS = 30_000;

const getPropertyFaqRetryCooldownMs = (errorStatus: number | null): number => {
    if (errorStatus === 429) {
        return RATE_LIMIT_COOLDOWN_MS;
    }

    return errorStatus === 503 ? CIRCUIT_BREAKER_COOLDOWN_MS : 0;
};

const getPropertyFaqErrorMessage = (errorStatus: number | null): string => {
    if (errorStatus === 429) {
        return "Osiągnięto limit pytań. Spróbuj ponownie za minutę.";
    }

    // 502 and 503 mean the DSA answer is unavailable, not that the request was wrong.
    if (errorStatus !== null && errorStatus >= 500 && errorStatus < 600) {
        return PROPERTY_FAQ_SERVER_ERROR_MESSAGE;
    }

    return "Nie udało się pobrać odpowiedzi. Spróbuj ponownie.";
};

const getPropertyFaqRetryLabel = (errorStatus: number | null, isRetryBlocked: boolean): string => {
    if (!isRetryBlocked) {
        return "Spróbuj ponownie";
    }

    return errorStatus === 429 ? "Spróbuj ponownie za minutę" : "Spróbuj ponownie za chwilę";
};

const getPropertyFaqErrorStatus = (error: unknown): number | null => {
    if (!error || typeof error !== "object") {
        return null;
    }

    const {status, originalStatus} = error as {status?: unknown; originalStatus?: unknown};

    if (typeof status === "number") {
        return status;
    }

    return typeof originalStatus === "number" ? originalStatus : null;
};

const accordionStyle = css({
    width: "100%"
});

const headerCollapsedStyle = css({
    borderRadius: 1
});

const headerExpandedStyle = css({
    backgroundColor: "var(--colors-background-100)"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    px: 2,
    pb: 2,
    backgroundColor: "var(--colors-background-100)"
});

const answerErrorStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 1
});
